import React from 'react';
import { useTranslation } from 'react-i18next';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import moment from 'moment';

import { ScenarioActiveOption } from 'features/residents/models/scenario-active-option';
import AcousticTimeFrames from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/acoustic-scenario-configuration/acoustic-time-frame-configuration/acoustic-time-frames';
import { useAcousticScenarioConfiguration } from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/acoustic-scenario-configuration/hooks';
import AcousticSettings from './acoustic-settings/acoustic-settings';
import styles from './acoustic-scenario-configuration.module.scss';

type Props = {
  scenarioTitle: string;
  index: number;
};

const AcousticScenarioConfiguration = ({ scenarioTitle, index }: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings');

  const {
    configuration,
    notInEditMode,
    tempConfigEnabledChanged,
    timeFrameConfigurationsChanged,
    temporaryValidUntilTimeChanged,
    updateResidentSettingEditFormScenario,
    tempConfigSoundThresholdChanged,
    tempConfigDurationChanged,
  } = useAcousticScenarioConfiguration({ index });

  const renderActiveAlarmOptions = [
    <MenuItem data-testid={`alarm-active`} key={`alarm-active`} value={ScenarioActiveOption.AlarmsOnly}>
      {t('scenarioConfiguration.alarmActive')}
    </MenuItem>,
  ];

  const titleDescription = t(`scenarioConfiguration.scenarioDescription`);

  return (
    <div className={styles.acousticScenarioConfigurationContainer}>
      <div className={styles.restoreRow}>
        <Typography variant="subtitle1">{`${scenarioTitle} ${titleDescription}`}</Typography>
      </div>
      <div className={styles.alarmActiveRow}>
        <FormControl className={styles.alarmFormControl}>
          <InputLabel id={styles.alarmActiveLabel}>{t('scenarioConfiguration.alarmActiveLabel')}</InputLabel>
          <Select
            inputProps={{ 'data-testid': 'AlarmActiveInput' }}
            id="alarmActiveInput"
            variant="outlined"
            className={styles.alarmActiveSelect}
            labelId={styles.alarmActiveLabel}
            label={t('scenarioConfiguration.alarmActiveLabel')}
            value={ScenarioActiveOption.AlarmsOnly}
            defaultValue={0}
            disabled
          >
            {renderActiveAlarmOptions.map((menuItem) => menuItem)}
          </Select>
        </FormControl>
      </div>

      <AcousticTimeFrames
        activeAllDay={configuration.sendAlarmAllDay}
        soundThreshold={configuration.acousticTimeFrameConfigurations?.[0].soundThreshold ?? 0}
        duration={configuration.acousticTimeFrameConfigurations?.[0].duration ?? 0}
        index={index}
        acousticConfiguration={configuration}
        valuesChanged={timeFrameConfigurationsChanged}
        timeFramesDescription={t('scenarioConfiguration.timeFramesDescriptionAlarm')}
        updateResidentSettingEditFormScenario={updateResidentSettingEditFormScenario}
      />
      <div className={styles.acousticSettings}>
        {configuration.sendAlarmAllDay && (
          <AcousticSettings
            acousticConfiguration={configuration}
            activeAllDay={configuration.sendAlarmAllDay}
            index={0}
            valuesChanged={timeFrameConfigurationsChanged}
            updateResidentSettingEditFormScenario={updateResidentSettingEditFormScenario}
            isTemporaryConfiguration={false}
          />
        )}

        <FormControl
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Typography variant="subtitle1">{t('acousticScenarioConfiguration.temporaryConfigurationEnabled')}</Typography>
          <Checkbox
            checked={configuration.temporaryConfigurationEnabled}
            disabled={notInEditMode}
            onChange={(_, enabled) => tempConfigEnabledChanged(enabled)}
          />
        </FormControl>

        {configuration.temporaryConfigurationEnabled && (
          <>
            <Typography variant="subtitle1">{t('acousticScenarioConfiguration.validUntil')}</Typography>
            <TimePicker
              label={t('scenarioConfiguration.to')}
              ampm={false}
              view={'hours'}
              views={['hours']}
              format="HH:mm"
              value={
                configuration.temporaryConfiguration?.validUntil
                  ? moment(configuration.temporaryConfiguration?.validUntil)
                  : moment().startOf('hour').add(1, 'hour')
              }
              onChange={temporaryValidUntilTimeChanged}
              disabled={notInEditMode}
              slotProps={{
                popper: {
                  className: 'time-picker-popper',
                },
              }}
            />
            {configuration.temporaryConfigurationEnabled && (
              <AcousticSettings
                acousticConfiguration={configuration}
                activeAllDay={configuration.sendAlarmAllDay}
                index={index}
                valuesChanged={timeFrameConfigurationsChanged}
                updateResidentSettingEditFormScenario={updateResidentSettingEditFormScenario}
                isTemporaryConfiguration={true}
                tempConfigSoundThresholdChanged={(value) => tempConfigSoundThresholdChanged(value as number)}
                tempConfigDurationChanged={(value) => tempConfigDurationChanged(value as number)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AcousticScenarioConfiguration;
